import ToolTip from "components/design/toolTip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { EMAIL } from "components/WhatsappSharedSlider/MessageInfoSlider";
import dayjs from "dayjs";
import { useMemo } from "react";
import FilingInfoIcon from "static/images/FilingInfo.svg";
import { useGetMessageEmailDetailsQuery } from "store/apis/chat";
import { MessageEmailDetailsType } from "types/Models/Chat";
import { capitalize } from "utils/capitalize";

export const MessageEmailDetails = ({ channelId }: { channelId: string }) => {
  const { data, isSuccess } = useGetMessageEmailDetailsQuery(
    { channelId: channelId },
    { skip: !channelId }
  );

  const emailGroupByBatch = useMemo(() => {
    return data
      ? data.reduce((acc: Record<string, MessageEmailDetailsType[]>, email) => {
          const batchId = email.email_grouping_uuid;
          if (!acc[batchId]) {
            acc[batchId] = [];
          }
          acc[batchId].push(email);
          return acc;
        }, {})
      : {};
  }, [data]);

  const formatDateWithTime = (date: string | null) => {
    if (!date) {
      return "-";
    }

    return dayjs(date).format("DD-MMM-YYYY, h:mma");
  };

  return (
    <Async.Root
      isEmpty={isSuccess && data.length === 0}
      isLoading={false}
      isSuccess={isSuccess}
    >
      <Async.Empty>{null}</Async.Empty>
      <Async.Success>
        <Accordion.Item value={EMAIL} key={EMAIL}>
          <Accordion.Trigger className="all:unset t-text-subtitle-sm t-text-text-60 t-flex t-gap-2 t-group t-w-full">
            Email sent to:
          </Accordion.Trigger>
          <Accordion.Content className="t-text-body-sm t-text-text-60 t-mt-3 t-grid t-gap-4">
            {Object.keys(emailGroupByBatch)?.map((emailDetailId) => (
              <div key={emailDetailId} className="t-space-y-3">
                <div>
                  <div className="t-flex t-items-center t-gap-6">
                    <div className="t-text-subtext-sm t-text-text-60">
                      {formatDateWithTime(
                        emailGroupByBatch[emailDetailId][0].email_sent_at
                      )}
                    </div>{" "}
                    <div className="t-max-w-56 t-flex-1 t-h-[1px] t-bg-separator-fade-right"></div>
                  </div>
                  <div className="t-text-body-sm t-text-text-30 t-mt-1">
                    Subject:{" "}
                    <span className="t-font-light">
                      {emailGroupByBatch[emailDetailId][0].email_subject || ""}
                    </span>
                  </div>
                </div>
                {emailGroupByBatch[emailDetailId].map((emailDetail) => (
                  <div
                    className="t-flex t-w-full t-gap-2"
                    key={emailDetail.email_sent_at}
                  >
                    <Avatar
                      src={emailDetail.profile_url}
                      alt={emailDetail.recipient_name || ""}
                      size="regular"
                    />
                    <div className="t-flex t-flex-col t-w-full">
                      <div className="t-flex t-justify-between t-items-center t-w-full">
                        <div className="t-text-body-sm t-text-text-60">
                          {emailDetail.recipient_name}
                        </div>
                        <div className="t-capitalize t-text-caption t-text-text-30">
                          {capitalize(emailDetail.email_status)}
                          <ToolTip
                            side="left"
                            text={
                              <div className="t-text-left">
                                <div>
                                  Sent:{" "}
                                  {formatDateWithTime(
                                    emailDetail.email_sent_at
                                  )}
                                </div>
                                <div className="t-flex t-items-center">
                                  <div>
                                    {capitalize(emailDetail.email_status)}
                                  </div>
                                  :{" "}
                                  {formatDateWithTime(
                                    emailDetail.email_status_updated_at
                                  )}
                                </div>
                              </div>
                            }
                          >
                            <img
                              src={FilingInfoIcon}
                              alt="info"
                              className="t-ml-1"
                            />
                          </ToolTip>
                        </div>
                      </div>
                      <div className="t-text-body-sm t-text-neutral-40 t-w-2/3 t-truncate">
                        {emailDetail.recipient_email}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </Accordion.Content>
        </Accordion.Item>
      </Async.Success>
    </Async.Root>
  );
};
